/**
 * Analytics Consent Manager
 * 
 * Управление согласием пользователя на использование аналитики (GDPR compliance)
 * Хранит состояние согласия в localStorage
 */

const CONSENT_STORAGE_KEY = 'analytics_consent'
const CONSENT_VERSION = '1.0'

export type ConsentStatus = 'granted' | 'denied' | 'pending'

export interface ConsentState {
  /** Согласие на аналитику */
  analytics: ConsentStatus
  /** Согласие на маркетинговые куки */
  marketing: ConsentStatus
  /** Версия согласия */
  version: string
  /** Дата последнего обновления */
  updatedAt: string
}

const DEFAULT_CONSENT: ConsentState = {
  analytics: 'pending',
  marketing: 'pending',
  version: CONSENT_VERSION,
  updatedAt: new Date().toISOString(),
}

type ConsentListener = (state: ConsentState) => void
const listeners: Set<ConsentListener> = new Set()

/**
 * Проверить доступность localStorage (SSR-safe)
 * Также обрабатывает случаи приватного режима браузера
 */
function isStorageAvailable (): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  
  try {
    const testKey = '__storage_test__'
    window.localStorage.setItem(testKey, testKey)
    window.localStorage.removeItem(testKey)
    return true
  }
  catch {
    // localStorage недоступен (приватный режим, отключены куки и т.д.)
    return false
  }
}

/**
 * Получить текущее состояние согласия
 */
export function getConsentState (): ConsentState {
  try {
    if (!isStorageAvailable()) {
      return DEFAULT_CONSENT
    }
    
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!stored) {
      return DEFAULT_CONSENT
    }
    
    const parsed = JSON.parse(stored) as ConsentState
    
    // Проверяем версию согласия - если устарело, сбрасываем
    if (parsed.version !== CONSENT_VERSION) {
      return DEFAULT_CONSENT
    }
    
    return parsed
  }
  catch {
    return DEFAULT_CONSENT
  }
}

/**
 * Сохранить состояние согласия
 */
function saveConsentState (state: ConsentState): void {
  try {
    if (!isStorageAvailable()) return
    
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state))
    
    // Уведомляем слушателей
    listeners.forEach((listener) => {
      try {
        listener(state)
      }
      catch (error) {
        console.error('[Consent] Listener error:', error)
      }
    })
  }
  catch (error) {
    console.error('[Consent] Error saving state:', error)
  }
}

/**
 * Проверить, дано ли согласие на аналитику
 */
export function hasAnalyticsConsent (): boolean {
  const state = getConsentState()
  return state.analytics === 'granted'
}

/**
 * Проверить, ожидается ли согласие (пользователь еще не принял решение)
 */
export function isConsentPending (): boolean {
  const state = getConsentState()
  return state.analytics === 'pending'
}

/**
 * Принять согласие на аналитику
 */
export function grantAnalyticsConsent (): void {
  const state = getConsentState()
  saveConsentState({
    ...state,
    analytics: 'granted',
    updatedAt: new Date().toISOString(),
  })
  
  // Обновляем consent в GA4
  updateGoogleConsent('granted')
  
  if (import.meta.env.DEV) {
    console.log('[Consent] Analytics consent granted')
  }
}

/**
 * Отклонить согласие на аналитику
 */
export function denyAnalyticsConsent (): void {
  const state = getConsentState()
  saveConsentState({
    ...state,
    analytics: 'denied',
    updatedAt: new Date().toISOString(),
  })
  
  // Обновляем consent в GA4
  updateGoogleConsent('denied')
  
  if (import.meta.env.DEV) {
    console.log('[Consent] Analytics consent denied')
  }
}

/**
 * Принять все согласия
 */
export function acceptAllConsent (): void {
  saveConsentState({
    analytics: 'granted',
    marketing: 'granted',
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  })
  
  updateGoogleConsent('granted')
  
  if (import.meta.env.DEV) {
    console.log('[Consent] All consents granted')
  }
}

/**
 * Отклонить все согласия (только необходимые куки)
 */
export function denyAllConsent (): void {
  saveConsentState({
    analytics: 'denied',
    marketing: 'denied',
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  })
  
  updateGoogleConsent('denied')
  
  if (import.meta.env.DEV) {
    console.log('[Consent] All consents denied')
  }
}

/**
 * Сбросить согласие (для тестирования)
 */
export function resetConsent (): void {
  try {
    if (typeof window === 'undefined') return
    localStorage.removeItem(CONSENT_STORAGE_KEY)
  }
  catch {
    // ignore
  }
}

/**
 * Подписаться на изменения согласия
 */
export function subscribeToConsent (listener: ConsentListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Обновить Google Consent Mode
 */
function updateGoogleConsent (status: 'granted' | 'denied'): void {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: status,
        ad_storage: status,
      })
    }
  }
  catch (error) {
    console.error('[Consent] Error updating Google consent:', error)
  }
}

/**
 * Initialise consent state on app start
 * Должен вызываться при инициализации приложения
 */
export function initConsent (): void {
  const state = getConsentState()
  
  // Если согласие в статусе pending, автоматически даём согласие
  // (для Казахстана не требуется явное согласие как в GDPR)
  if (state.analytics === 'pending') {
    grantAnalyticsConsent()
    return
  }
  
  // Согласие уже было дано/отклонено, обновляем Google Consent Mode
  updateGoogleConsent(state.analytics === 'granted' ? 'granted' : 'denied')
  
  if (import.meta.env.DEV) {
    console.log('[Consent] Initialized with state:', state)
  }
}
